import { Measurement } from "@cur8/rich-entity";
import { MetricConfig, MetricGroup } from "./measurementConfig";
import { round1 } from "./measurementConversion";
import { getImplausibility } from "./measurementPlausibility";

// Scan Result Handbook, updated 2026-08-26 by Yuan Tan. The two limbs are
// deliberately different: arms are normal to 14 so 15 triggers, ankles are
// normal to 20 so only 21 does. This is the source of truth over the ticket text
// and over Yuan's earlier ">/= 20" comment, both of which disagree with it.
const INTER_ARM_LIMIT_MMHG = 15;
const INTER_ANKLE_LIMIT_MMHG = 20;

type SideToSideRule = {
  leftMetricName: string;
  rightMetricName: string;
  // Arms compare systolic only; ankles have a single systolic-equivalent value.
  dimensionName: string;
  limitMmHg: number;
  limitIsInclusive: boolean;
  // Copy: "Systolic difference: 110" on the tile, "Systolic pressure difference
  // is ..." in the banner, "the left arm reading" in a per-reading sentence.
  differenceLabel: string;
  differenceSubject: string;
  limbNoun: string;
};

const SIDE_TO_SIDE_RULES: SideToSideRule[] = [
  {
    leftMetricName: "cardio.brachial_pressure.left",
    rightMetricName: "cardio.brachial_pressure.right",
    dimensionName: "systolic",
    limitMmHg: INTER_ARM_LIMIT_MMHG,
    limitIsInclusive: true,
    differenceLabel: "Systolic difference",
    differenceSubject: "Systolic pressure",
    limbNoun: "arm",
  },
  {
    leftMetricName: "cardio.ankle_pressure.left",
    rightMetricName: "cardio.ankle_pressure.right",
    dimensionName: "value",
    limitMmHg: INTER_ANKLE_LIMIT_MMHG,
    limitIsInclusive: false,
    differenceLabel: "Difference",
    differenceSubject: "Ankle pressure",
    limbNoun: "ankle",
  },
];

export const CAUTION_TITLE = "Verify value";

export type MeasurementCaution = {
  // Tile eyebrow, kept short for a third of the grid.
  title: string;
  bannerTitle: string;
  // Banner prose. The tile shows the numbers instead.
  sentences: string[];
  // Absent when the sides agree and only a reading is at fault. Parts rather
  // than a formatted string, so the tile and the banner can lay the same numbers
  // out differently.
  difference?: {
    label: string;
    amount: number;
    limit: number;
    limitIsInclusive: boolean;
  };
  implausibleMetricNames: string[];
};

function readDimension(
  measurement: Measurement | undefined,
  dimensionName: string
): number | undefined {
  return measurement?.dimensions.find((d) => d.name === dimensionName)?.value;
}

function findRule(group: MetricGroup) {
  const metricNames = new Set(group.metrics.map((m) => m.metricName));
  return SIDE_TO_SIDE_RULES.find(
    (r) =>
      metricNames.has(r.leftMetricName) && metricNames.has(r.rightMetricName)
  );
}

function getPairDifference(
  rule: SideToSideRule,
  existingByMetric: Map<string, Measurement>
) {
  const left = readDimension(
    existingByMetric.get(rule.leftMetricName),
    rule.dimensionName
  );
  const right = readDimension(
    existingByMetric.get(rule.rightMetricName),
    rule.dimensionName
  );
  if (left == null || right == null) {
    return undefined;
  }

  // Compared raw, rounded only for display. Rounding first would let 14.96 read
  // as 15.0 and trip the arms' inclusive limit, which is the opposite of the
  // handbook's intent.
  const difference = Math.abs(left - right);
  const overLimit = rule.limitIsInclusive
    ? difference >= rule.limitMmHg
    : difference > rule.limitMmHg;
  return overLimit ? { difference } : undefined;
}

// Flags a group on an unbelievable reading, or on the two sides disagreeing, or
// both. Either alone is enough, which is what catches two matched bad readings:
// their difference is small, so only the reading check sees them. Keyed on metric
// name rather than device, so a typed pair is checked like an imported one.
export function getMeasurementCaution(
  group: MetricGroup,
  existingByMetric: Map<string, Measurement>
): MeasurementCaution | undefined {
  const rule = findRule(group);

  const readingName = (metric: MetricConfig) => {
    const side = metric.variant?.value.toLowerCase();
    return side && rule ? `${side} ${rule.limbNoun}` : metric.label;
  };

  const flagged = group.metrics.flatMap((metric) => {
    const implausibility = getImplausibility(
      metric,
      existingByMetric.get(metric.metricName)
    );
    if (!implausibility) {
      return [];
    }
    // Only the arithmetic case may claim the value is not real. A reading
    // outside the handbook's range might still be what the member measured.
    const sentence =
      implausibility.kind === "impossible"
        ? `The ${readingName(metric)} reading is not possible: diastolic is higher than systolic.`
        : `The ${readingName(metric)} reading is unusually ${implausibility.direction}.`;
    return [
      { metricName: metric.metricName, kind: implausibility.kind, sentence },
    ];
  });

  const implausibleMetricNames = flagged.map((f) => f.metricName);
  const pair = rule ? getPairDifference(rule, existingByMetric) : undefined;

  if (implausibleMetricNames.length === 0 && !pair) {
    return undefined;
  }

  // Wording tracks each limb's inclusivity, so it cannot contradict the limit
  // shown beside it.
  const differenceSentence =
    pair && rule
      ? `${rule.differenceSubject} difference is ${
          rule.limitIsInclusive
            ? `${rule.limitMmHg} mmHg or more`
            : `more than ${rule.limitMmHg} mmHg`
        }.`
      : undefined;

  return {
    title: CAUTION_TITLE,
    bannerTitle: "Retest before the member leaves the room",
    // An out-of-range reading goes unsaid when the difference already explains
    // the flag. An impossible one is always said, since the difference sentence
    // cannot describe it, and with no difference sentence the reading is the
    // only reason there is.
    sentences: [
      ...flagged
        .filter((f) => f.kind === "impossible" || !differenceSentence)
        .map((f) => f.sentence),
      differenceSentence,
      "Please check the cuff placement and repeat.",
    ].filter((sentence): sentence is string => sentence != null),
    difference:
      pair && rule
        ? {
            label: rule.differenceLabel,
            amount: round1(pair.difference),
            limit: rule.limitMmHg,
            limitIsInclusive: rule.limitIsInclusive,
          }
        : undefined,
    implausibleMetricNames,
  };
}
